import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Alert, IconButton,
  InputAdornment, Divider, Stack,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Google, Apple } from '@mui/icons-material';
import useAuthStore from '../../store/useStore';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function LoginPage() {
  const navigate = useNavigate();
  const { login, socialLogin } = useAuthStore();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inactivityMsg, setInactivityMsg] = useState(false);
  const googleBtnRef = useRef(null);

  // Check if user was logged out due to inactivity or expired session
  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason === 'inactivity' || reason === 'expired') {
      setInactivityMsg(true);
      sessionStorage.removeItem('logout_reason');
    }
  }, []);

  const redirectAfterLogin = useCallback((user) => {
    if (user.role === 'super_admin' || user.role === 'rent_control') {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form);
      redirectAfterLogin(user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Google credential callback
  const handleGoogleCredential = useCallback(async (response) => {
    setLoading(true);
    setError('');
    try {
      const user = await socialLogin('google', response.credential);
      redirectAfterLogin(user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  }, [socialLogin, redirectAfterLogin]);

  const handleGoogleLogin = () => {
    if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'YOUR_GOOGLE_CLIENT_ID_HERE') {
      setError('Google Sign-In is not configured yet. Set REACT_APP_GOOGLE_CLIENT_ID in your .env file.');
      return;
    }
    /* global google */
    if (window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback: render the Google button
          if (googleBtnRef.current) {
            googleBtnRef.current.innerHTML = '';
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              theme: 'outline', size: 'large', width: '100%', text: 'signin_with',
            });
          }
        }
      });
    } else {
      setError('Google Sign-In SDK not loaded. Please refresh and try again.');
    }
  };

  const handleAppleLogin = () => {
    setError('Apple Sign-In requires an Apple Developer account and domain verification. Configure your Apple Service ID to enable this feature.');
  };

  useEffect(() => {
    // Load Google Identity Services script
    if (GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'YOUR_GOOGLE_CLIENT_ID_HERE' && !document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight={700} color="primary" component={Link} to="/" sx={{ textDecoration: 'none' }}>
            CampusNest
          </Typography>
        </Box>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>Welcome Back</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to access your account
          </Typography>

          {inactivityMsg && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setInactivityMsg(false)}>
              Your session expired due to inactivity. Please sign in again.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Email Address" type="email" required
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment> }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth label="Password" type={showPassword ? 'text' : 'password'} required
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <Box textAlign="right" mb={2}>
              <Typography variant="body2" color="primary" component={Link} to="/forgot-password" sx={{ textDecoration: 'none' }}>
                Forgot password?
              </Typography>
            </Box>

            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ py: 1.5, mb: 2 }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}><Typography variant="body2" color="text.secondary">or continue with</Typography></Divider>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              fullWidth variant="outlined" size="large" startIcon={<Google />}
              onClick={handleGoogleLogin} disabled={loading}
              sx={{ py: 1.2, textTransform: 'none', borderColor: '#ddd', color: '#333',
                '&:hover': { borderColor: '#4285F4', bgcolor: '#f5f5f5' } }}
            >
              Google
            </Button>
            <Button
              fullWidth variant="outlined" size="large" startIcon={<Apple />}
              disabled={loading}
              sx={{ py: 1.2, textTransform: 'none', borderColor: '#ddd', color: '#333',
                '&:hover': { borderColor: '#000', bgcolor: '#f5f5f5' } }}
              onClick={handleAppleLogin}
            >
              Apple
            </Button>
          </Stack>
          {/* Google rendered-button fallback */}
          <Box ref={googleBtnRef} sx={{ mb: 1, display: 'flex', justifyContent: 'center' }} />

          <Typography variant="body2" textAlign="center">
            Don't have an account?{' '}
            <Typography component={Link} to="/register" variant="body2" color="primary" fontWeight={600} sx={{ textDecoration: 'none' }}>
              Create Account
            </Typography>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginPage;
